import React, { useState, useEffect } from 'react';
import { fmtDate } from '../utils/format';

// Presentational, data-driven render of a scoped work report — shared by SharedReportPage
// (token-driven, public) and the owner's in-app "View" preview (ReportPage.jsx, authenticated).
export default function ReportView({ data }) {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const handler = e => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  const { month, toMonth, business, entries=[], scopedPersonName } = data||{};
  const fmtM = m => m ? new Date(m+'-01').toLocaleString('default',{month:'long',year:'numeric'}) : '';
  const periodLabel = toMonth && toMonth!==month ? `${fmtM(month)} – ${fmtM(toMonth)}` : fmtM(month);
  const clientIds = [...new Set(entries.map(e=>e.client_id))];

  return (
    <div style={{maxWidth:860,margin:'0 auto',padding:'20px 16px',fontFamily:'-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',color:'#1A1916'}}>
      <div style={{textAlign:'center',marginBottom:24,padding:'20px 20px 16px',background:'#EBF1FD',borderRadius:10}}>
        {business?.logo_path
          ? <img src={`/uploads/logo/${business.logo_path}`} alt={business?.name} style={{height:48,maxWidth:200,objectFit:'contain',marginBottom:10,display:'block',margin:'0 auto 10px'}} />
          : <div style={{fontSize:22,fontWeight:800,color:'#2D6BE4',marginBottom:4}}>{business?.name}</div>
        }
        <div style={{fontSize:13,color:'#6B6760',marginTop:4}}>Work report — {periodLabel}</div>
        {scopedPersonName&&<div style={{fontSize:12,marginTop:6,display:'inline-block',background:'#2D6BE4',color:'#fff',borderRadius:20,padding:'3px 12px'}}>{scopedPersonName}</div>}
      </div>

      {entries.length===0&&(
        <div style={{textAlign:'center',padding:'40px 16px',color:'#6B6760',border:'1px dashed #E2DDD6',borderRadius:10,background:'#fff'}}>
          <div style={{fontSize:28,marginBottom:8}}>📭</div>
          <strong style={{color:'#1A1916'}}>No work logged{scopedPersonName?` for ${scopedPersonName}`:''} in {month?new Date(month+'-01').toLocaleString('default',{month:'long',year:'numeric'}):'this month'}</strong>
          <div style={{fontSize:12,marginTop:6}}>Try another month — work only appears here once it has been logged for this {scopedPersonName?'person':'client'}.</div>
        </div>
      )}

      {clientIds.map(cid=>{
        const cl=entries.filter(e=>e.client_id===cid);
        const clientName=cl[0]?.client_name||'';
        const byCat={};
        cl.forEach(e=>{if(!byCat[e.category_name])byCat[e.category_name]=0;byCat[e.category_name]+=e.quantity;});

        return (
          <div key={cid} style={{border:'1px solid #E2DDD6',borderRadius:10,marginBottom:16,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',background:'#F0EEE9',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <strong style={{fontSize:14}}>{clientName}</strong>
              <span style={{fontSize:12,color:'#6B6760'}}>{cl.reduce((s,e)=>s+e.quantity,0)} jobs</span>
            </div>
            <div style={{padding:'12px 16px'}}>
              {/* Category summary */}
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
                {Object.entries(byCat).map(([cat,qty])=>(
                  <span key={cat} style={{background:'#F0EEE9',padding:'3px 10px',borderRadius:20,fontSize:12}}>{cat} ×{qty}</span>
                ))}
              </div>
              {/* Detailed list */}
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr>{['Date','Description','Category','Qty',''].map((h,i)=><th key={i} style={{textAlign:'left',padding:'6px 0',borderBottom:'1px solid #E2DDD6',fontSize:11,color:'#6B6760',fontWeight:600}}>{h}</th>)}</tr></thead>
                <tbody>
                  {cl.map(e=>(
                    <tr key={e.id} style={e.corrections>0?{background:'#FDF0EE'}:{}}>
                      <td style={{padding:'7px 0',borderBottom:'1px solid #F0EEE9',color:'#6B6760',whiteSpace:'nowrap'}}>{fmtDate(e.work_date)}</td>
                      <td style={{padding:'7px 0',borderBottom:'1px solid #F0EEE9'}}>
                        {e.node_name&&(
                          <span style={{marginRight:4}}>
                            {e.parent_node_name
                              ? <span style={{fontSize:10,color:'#6B6760'}}>{e.parent_node_name} › <strong style={{color:'#1A1916'}}>{e.node_name}</strong>: </span>
                              : <span style={{fontSize:10,background:'#EBF1FD',color:'#2D6BE4',borderRadius:3,padding:'1px 5px',fontWeight:600,marginRight:4}}>{e.node_name}</span>
                            }
                          </span>
                        )}
                        {e.description}
                        {e.corrections>0&&(
                          <span style={{display:'inline-block',background:'#FDF0EE',border:'1px solid #F5C6C0',borderRadius:6,marginLeft:6,padding:'3px 8px',verticalAlign:'middle'}}>
                            <span style={{color:'#C0392B',fontWeight:700,fontSize:10}}>+{e.corrections} correction{e.corrections>1?'s':''}</span>
                            {(e.correction_lines||[]).filter(l=>l.type_name).length>0 ? (
                              <span>
                                {(e.correction_lines||[]).filter(l=>l.type_name).map((l,i)=>{
                                  const base=parseFloat(l.price||0)*l.count;
                                  const gst=base*parseFloat(l.gst_percent||0)/100;
                                  return (
                                    <span key={i} style={{display:'block',fontSize:10,color:'#7A2020',marginTop:2}}>
                                      {l.type_name} ×{l.count}: ₹{base.toLocaleString('en-IN')} + {l.gst_percent}% GST = <strong>₹{(base+gst).toLocaleString('en-IN')}</strong>
                                      {l.reason&&<span style={{color:'#9A4040',fontWeight:400}}> ({l.reason})</span>}
                                    </span>
                                  );
                                })}
                                {(()=>{
                                  const lines=(e.correction_lines||[]).filter(l=>l.type_name);
                                  const totalBase=lines.reduce((s,l)=>s+parseFloat(l.price||0)*l.count,0);
                                  const totalGst=lines.reduce((s,l)=>s+(parseFloat(l.price||0)*l.count*parseFloat(l.gst_percent||0)/100),0);
                                  return lines.length>1&&<span style={{display:'block',fontSize:10,color:'#C0392B',fontWeight:700,marginTop:3,borderTop:'1px solid #F5C6C0',paddingTop:2}}>Total: ₹{totalBase.toLocaleString('en-IN')} + GST = ₹{(totalBase+totalGst).toLocaleString('en-IN')}</span>;
                                })()}
                              </span>
                            ) : e.correction_reason ? (
                              <span style={{fontSize:10,color:'#7A2020',display:'block',marginTop:2}}>{e.correction_reason}</span>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td style={{padding:'7px 0',borderBottom:'1px solid #F0EEE9',color:'#6B6760'}}>{e.category_name}</td>
                      <td style={{padding:'7px 0',borderBottom:'1px solid #F0EEE9',textAlign:'right'}}>{e.quantity}</td>
                      <td style={{padding:'7px 4px',borderBottom:'1px solid #F0EEE9',textAlign:'right',width:40}}>
                        {e.proof_image && (
                          <img
                            src={`/uploads/proofs/${e.proof_image}`}
                            alt="proof"
                            onClick={() => setLightbox(`/uploads/proofs/${e.proof_image}`)}
                            style={{width:32,height:32,objectFit:'cover',borderRadius:5,border:'1px solid #E2DDD6',cursor:'zoom-in',display:'block',marginLeft:'auto'}}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      <div style={{textAlign:'center',fontSize:11,color:'#A09C96',marginTop:20,paddingTop:16,borderTop:'1px solid #E2DDD6'}}>
        Generated by 8D Studios Business OS™ · {business?.name} · Work pricing not included · Correction charges shown where applicable.
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20,cursor:'zoom-out'}}>
          <img src={lightbox} alt="proof" style={{maxWidth:'100%',maxHeight:'90vh',borderRadius:8,boxShadow:'0 8px 40px rgba(0,0,0,0.5)',cursor:'default'}} onClick={e=>e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} style={{position:'absolute',top:16,right:20,background:'rgba(255,255,255,0.15)',border:'none',color:'#fff',fontSize:18,borderRadius:'50%',width:36,height:36,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1,padding:0}}>✕</button>
        </div>
      )}
    </div>
  );
}
